import { AdmRefreshGlobalSettings, AdmRefreshGlobalSettingsData } from '@pubstack/common/src/adm/refresh-global-settings'
import { LINE_ITEM_TYPES, LineItemTypeName } from '@pubstack/common/src/gam/lineItemType'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRefreshRules } from '~/api/adm-api.hook'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { Link } from '~/components/Link'
import { useToast } from '~/components/Toast/useToasts'
import { PureAdmRefreshGlobalSettingsPage } from './PureAdmRefreshGlobalSettingsPage'

export const AdmRefreshGlobalSettingsPage: FC = () => {
  const navigate = useNavigate()
  const { loadExcludedCampaigns, fetchGlobalSettings, saveGlobalSettings: apiSave, saveExclusionLineItemTypes } = useRefreshRules(null)
  const [globalSettings, setGlobalSettings] = useState<AdmRefreshGlobalSettingsData>({
    excludedCampaignsData: null,
    excludedCampaignsUnavailableReason: null,
    refreshGlobalSettings: null,
    excludedLineItemTypes: [],
    bidders: [],
  })
  const toast = useToast()
  const { isAdmin: isPubstackRole } = useAuthenticatedUser()

  const onLoadExcludedCampaigns = async () => {
    try {
      await loadExcludedCampaigns()
      const excludedCampaignsData = globalSettings.excludedCampaignsData ? { ...globalSettings.excludedCampaignsData, canLambdaBeLaunched: false } : globalSettings.excludedCampaignsData
      setGlobalSettingsData({ ...globalSettings, excludedCampaignsData })
      toast.success('The fetch of the excluded campaign list has started.')
    } catch (_e) {
      toast.alert('An error occurred while trying to load the excluded campaign.')
    }
  }

  const setGlobalSettingsData = (data: AdmRefreshGlobalSettingsData) => {
    setGlobalSettings({
      ...data,
      refreshGlobalSettings:
        data.refreshGlobalSettings !== null
          ? {
              ...data.refreshGlobalSettings,
              settings: data.refreshGlobalSettings.settings.map((setting) => ({
                ...setting,
                bidders: setting.bidders.map((bidder) => data.bidders.find((b) => b.codeOrAlias === bidder)?.displayName ?? ''),
              })),
            }
          : null,
    })
  }

  async function getGlobalSettings() {
    if (fetchGlobalSettings.loading) {
      fetchGlobalSettings.abort()
    }
    try {
      const res = await fetchGlobalSettings.get()
      setGlobalSettingsData(res)
    } catch (_e) {
      toast.alert('An error occurred while trying to retrieve the global settings data.')
    }
  }

  async function saveGlobalSettings(newGlobalSettings: AdmRefreshGlobalSettings, isFormDirty: boolean) {
    try {
      if (newGlobalSettings.id) {
        await apiSave.put(newGlobalSettings)
      } else {
        await apiSave.post(newGlobalSettings)
      }
      toast.admSuccess('Settings', isFormDirty, navigate)
      if (newGlobalSettings.id) {
        setGlobalSettingsData({ ...globalSettings, refreshGlobalSettings: newGlobalSettings })
      } else {
        await getGlobalSettings()
      }
    } catch (exception) {
      toast.alert('An error occurred while saving the global settings.')
      throw exception
    }
  }

  async function saveExcludedLineItemTypes(newLineItemTypes: LineItemTypeName[]) {
    try {
      await saveExclusionLineItemTypes.put(newLineItemTypes)
      const displayLineItemTypes = newLineItemTypes
        .map((name) => {
          return LINE_ITEM_TYPES.find((type) => type.name === name)?.displayName ?? name
        })
        .join(', ')
      const updateMessage =
        newLineItemTypes.length > 0 ? (
          <>
            <strong>{displayLineItemTypes}</strong> line items will not be refreshed anymore.
          </>
        ) : (
          'No refresh exclusion per line item type will happen anymore.'
        )
      toast.success(
        <>
          {updateMessage} Click on <Link onClick={() => onLoadExcludedCampaigns()} label={'Load excluded campaigns'} trackingLabel={'adm-deployment-center-navigate'} /> for an immediate effect.
        </>
      )

      setGlobalSettingsData({ ...globalSettings, excludedLineItemTypes: newLineItemTypes })
    } catch (_e) {
      toast.alert('An error occurred while saving the excluded line item types.')
      // Force a reference change to trigger a re-render and update the UI accordingly
      setGlobalSettingsData({ ...globalSettings, excludedLineItemTypes: [...globalSettings.excludedLineItemTypes] })
    }
  }

  useEffect(() => {
    getGlobalSettings()
  }, [])

  return (
    <PureAdmRefreshGlobalSettingsPage
      globalSettings={globalSettings}
      onLoadExcludedCampaigns={onLoadExcludedCampaigns}
      onSaveGlobalSettings={saveGlobalSettings}
      onSaveExcludedLineItemTypes={saveExcludedLineItemTypes}
      isLoading={fetchGlobalSettings.loading}
      isPubstackRole={isPubstackRole}
    />
  )
}
